import { CardContainer } from "@/components/commons/CardContainer";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Tag } from "@/components/elements/Tag";
import { TopPage } from "@/components/elements/TopPage";
import { DataRegistrationStats } from "@/features/learning-content/components/DataRegistrationStats";
import { QuestionDataTable } from "@/features/learning-content/components/QuestionDataTable";
import { SubjectInfoCard } from "@/features/learning-content/components/SubjectInfoCard";
import { WordDataTable } from "@/features/learning-content/components/WordDataTable";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { useSubjectDetail } from "@/features/learning-content/hooks/useSubjectDetail";
import { useUpdateSubjectMemo } from "@/features/learning-content/hooks/useUpdateSubjectMemo";
import { useVocabularyList } from "@/features/learning-content/hooks/useVocabularyList";
import { useQuestionList } from "@/features/learning-content/hooks/useQuestionList";
import { importQuestionsApi, importVocabulariesApi, toggleSubjectStatusApi } from "@/api/services/admin/learning-content";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { STATUS_LABEL } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useToast } from "@/hooks/useToast";
import { showError } from "@/utils/error";
import { fetchSignedUrlApi } from "@/api/services/global/file";
import { TGetSubjectDetailDto } from "@/api/services/admin/learning-content/dtos/get-subject-detail.dto";
import { convertSubjectDetailToInfo } from "@/features/learning-content/utils/converts";
import { AppRoutes } from "@/routes/config";
import { getFileNameFromPath } from "@/utils/file";
import { getDynamicRoute } from "@/utils/url";
import { IconCheckCircleFill } from "@/assets/icons";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, IconButton, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

type TSubjectDetailPageProps = {};

// Subject detail page - displays subject information, word data, and question data
export const SubjectDetailPage: FC<TSubjectDetailPageProps> = () => {
  const { id, subjectId } = useParams();
  const navigate = useNavigate();
  const { data: industryData } = useIndustryDetail(id);
  const { data: subjectData } = useSubjectDetail(subjectId);
  const vocabList = useVocabularyList(subjectId);
  const questionList = useQuestionList(subjectId);
  const [isSyncingVocabulary, setIsSyncingVocabulary] = useState(false);
  const updateMemo = useUpdateSubjectMemo(subjectId);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isToggling, setIsToggling] = useState(false);

  const isEnabled = subjectData?.isActive ?? false;

  const handleToggleStatus = async () => {
    if (!subjectId) return;
    setIsToggling(true);
    try {
      await toggleSubjectStatusApi(subjectId, { isActive: !isEnabled });
      toast.success(API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_SUBJECT_DETAIL, subjectId] });
    } catch (error) {
      showError(error);
    } finally {
      setIsToggling(false);
    }
  };

  const handleMemoBlur = (value: string) => {
    if (!subjectData) return;
    updateMemo.mutate({ name: subjectData.name, memo: value });
  };

  const handleSyncVocabulary = subjectId ? () => syncVocabulary(subjectId) : undefined;

  const handleSyncQuestions = subjectId ? () => syncQuestions(subjectId) : undefined;

  const syncVocabulary = async (id: string) => {
    setIsSyncingVocabulary(true);
    try {
      const result = await importVocabulariesApi(id);
      if (result.data.message) toast.success(result.data.message);
      vocabList.refetch();
    } catch (error) {
      showError(error);
    } finally {
      setIsSyncingVocabulary(false);
    }
  };

  const syncQuestions = async (id: string) => {
    try {
      const result = await importQuestionsApi(id);
      if (result.data.message) toast.success(result.data.message);
      questionList.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const handleBack = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } else {
      navigate(AppRoutes.learningContent);
    }
  };

  const handleEditSubjectInfo = () => {
    if (id && subjectId) {
      navigate(getDynamicRoute(AppRoutes.learningContentSubjectEdit, { id, subjectId }));
    }
  };

  const subjectInfo = useMemo(() => {
    if (!subjectData) return null;
    return convertSubjectDetailToInfo(subjectData, industryData?.name);
  }, [subjectData, industryData?.name]);

  const stats = useMemo(() => {
    if (!subjectData?.dataStatistics) return null;
    const ds = subjectData.dataStatistics;
    return {
      vocabularyCount: ds.vocabularyCount ?? 0,
      difficulty1QuestionCount: ds.difficulty1QuestionCount ?? 0,
      difficulty2QuestionCount: ds.difficulty2QuestionCount ?? 0,
      difficulty3QuestionCount: ds.difficulty3QuestionCount ?? 0,
      difficulty4QuestionCount: ds.difficulty4QuestionCount ?? 0,
      difficulty5QuestionCount: ds.difficulty5QuestionCount ?? 0,
      vocabularyLastUpdater: ds.vocabularyLastUpdater ?? "",
      vocabularyLastUpdatedAt: ds.vocabularyLastUpdatedAt ?? "",
      questionLastUpdater: ds.questionLastUpdater ?? "",
      questionLastUpdatedAt: ds.questionLastUpdatedAt ?? "",
    };
  }, [subjectData?.dataStatistics]);

  const subjectName = subjectData?.name ?? "";
  const industryName = industryData?.name ?? "";

  if (!subjectData) {
    return null;
  }

  return (
    <DetailWrapper gap={4}>
      <TopPage
        title={
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="head32Bold">
              {industryName}のコンテンツ管理 - {subjectName}
            </Typography>
            <Tag
              variant="bold"
              color={isEnabled ? "#C8E6C9" : "#ABB4B6"}
              title={isEnabled ? "有効" : STATUS_LABEL.DISABLED}
              sx={{ color: isEnabled ? "#2E7D32" : "#fff" }}
            />
          </Stack>
        }
        description={
          <Typography variant="body14">
            分野情報やコンテンツを編集できます。分野およびコンテンツを有効化するには最低でも1つ以上のWEEKが有効である必要があります。
          </Typography>
        }
        enablePageBack={false}
      />

      <MenuNavigate
        menuPrefix={
          <Box sx={{ p: "16px 16px 12px 16px" }}>
            <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
              {subjectName}
            </Typography>
          </Box>
        }
        contents={[
          {
            title: "WEEK情報",
            content: subjectInfo ? <SubjectInfoCard info={subjectInfo} onMemoBlur={handleMemoBlur} isSavingMemo={updateMemo.isPending} /> : null,
          },
          {
            title: "現在のデータ登録数",
            hideMenu: true,
            content: stats ? <DataRegistrationStats stats={stats} /> : null,
          },
          {
            title: "単語データ",
            content: (
              <WordDataTable
                isLoading={vocabList.isLoading || isSyncingVocabulary}
                isFetching={vocabList.isFetching}
                data={vocabList.data}
                totalPage={vocabList.totalPage}
                totalCount={vocabList.totalCount}
                page={vocabList.page}
                showImageColumn
                onPageChange={vocabList.onPageChange}
                onOpenSheet={
                  subjectData?.vocabularySpreadsheetUrl
                    ? () => window.open(subjectData.vocabularySpreadsheetUrl!, "_blank", "noopener,noreferrer")
                    : undefined
                }
                onSync={handleSyncVocabulary}
              />
            ),
          },
          {
            title: "問題データ",
            content: (
              <QuestionDataTable
                isLoading={questionList.isLoading}
                isFetching={questionList.isFetching}
                data={questionList.data}
                totalPage={questionList.totalPage}
                totalCount={questionList.totalCount}
                page={questionList.page}
                onPageChange={questionList.onPageChange}
                onOpenSheet={
                  subjectData?.questionSpreadsheetUrl
                    ? () => window.open(subjectData.questionSpreadsheetUrl!, "_blank", "noopener,noreferrer")
                    : undefined
                }
                onSync={handleSyncQuestions}
              />
            ),
          },
          {
            title: "テキスト解説",
            hideMenu: true,
            content: <TextExplanationInfo data={subjectData} />,
          },
        ]}
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm-round" variant="secondary" onClick={handleBack}>
                戻る
              </Button>
              <Stack direction="row" gap={2}>
                <Button variant="outline" onClick={handleEditSubjectInfo}>
                  WEEK情報を編集
                </Button>
                <Button
                  variant={"primary"}
                  onClick={handleToggleStatus}
                  startIcon={<IconCheckCircleFill />}
                  disabled={isToggling || isEnabled || vocabList.totalCount === 0 || questionList.totalCount === 0}
                >
                  WEEKを有効化
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        }
      />
    </DetailWrapper>
  );
};

// Read-only display of textbook file info for subject detail page
const TextExplanationInfo: FC<{ data?: TGetSubjectDetailDto }> = ({ data }) => {
  const fileName = data?.textbookFileUrl ? getFileNameFromPath(data.textbookFileUrl) : null;

  const handleOpenFile = useCallback(async () => {
    if (!data?.textbookFileUrl) return;
    try {
      const res = await fetchSignedUrlApi(data.textbookFileUrl);
      window.open(res.data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      showError(error);
    }
  }, [data?.textbookFileUrl]);

  const handleDownloadFile = useCallback(async () => {
    if (!data?.textbookFileUrl) return;
    try {
      const res = await fetchSignedUrlApi(data.textbookFileUrl);
      // Fetch as blob to force download for cross-origin signed URLs
      const blob = await fetch(res.data.signedUrl).then((r) => r.blob());
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName ?? "textbook";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showError(error);
    }
  }, [data?.textbookFileUrl, fileName]);

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          テキスト解説
        </Typography>

        {/* File name with preview and download icons */}
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body14" sx={{ fontWeight: 500 }}>
            {fileName ?? "未登録"}
          </Typography>
          {data?.textbookFileUrl && (
            <>
              <IconButton size="small" onClick={handleOpenFile} sx={{ color: "#48A0B2" }}>
                <SearchOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton size="small" onClick={handleDownloadFile} sx={{ color: "#48A0B2" }}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </>
          )}
        </Stack>

        {/* Internal memo section */}
        {data?.textbookMemo && (
          <Stack gap={0.5}>
            <Typography variant="body14" sx={{ color: "text.secondary" }}>
              テキスト解説に関する社内メモ
            </Typography>
            <Typography variant="body14" sx={{ fontWeight: 600 }}>
              {data.textbookMemo}
            </Typography>
          </Stack>
        )}
      </Stack>
    </CardContainer>
  );
};

const DetailWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
